import React from 'react';
import { DiceRoll, MoveOption } from './gameTypes';
import './DiceComponent.css';

interface DiceComponentProps {
  diceRoll: DiceRoll | null;
  isRolling: boolean;
  canRoll: boolean;
  moveOptions: MoveOption[];
  selectedMoveOption: MoveOption | null;
  onRoll: () => void;
  onMoveOptionSelect: (option: MoveOption) => void;
  onExecuteMove: () => void;
  gameVariant: 'classic' | 'naija';
  selectedDiceValue: number | null;
  onDiceValueSelect: (value: number) => void;
  availableMoves: { [diceValue: number]: number[] };
}

const DiceComponent: React.FC<DiceComponentProps> = ({
  diceRoll,
  isRolling,
  canRoll,
  moveOptions,
  selectedMoveOption,
  onRoll,
  onMoveOptionSelect,
  onExecuteMove,
  gameVariant,
  selectedDiceValue,
  onDiceValueSelect,
  availableMoves,
}) => {
  const renderDice = () => {
    if (isRolling) {
      return (
        <div className="dice-container rolling">
          {gameVariant === 'naija' ? (
            <>
              <div className="dice">🎲</div>
              <div className="dice">🎲</div>
            </>
          ) : (
            <div className="dice">🎲</div>
          )}
        </div>
      );
    }

    if (!diceRoll) {
      return (
        <div className="dice-container" onClick={canRoll ? onRoll : undefined}>
          {gameVariant === 'naija' ? (
            <>
              <div className={`dice ${canRoll ? 'clickable' : ''}`}>🎲</div>
              <div className={`dice ${canRoll ? 'clickable' : ''}`}>🎲</div>
            </>
          ) : (
            <div className={`dice ${canRoll ? 'clickable' : ''}`}>🎲</div>
          )}
        </div>
      );
    }

    return (
      <div className="dice-container">
        {diceRoll.values.map((value, index) => (
          <div key={index} className="dice rolled">
            {value}
          </div>
        ))}
      </div>
    );
  };

  const renderNaijaDiceOptions = () => {
    if (!diceRoll || gameVariant !== 'naija' || diceRoll.values.length !== 2) {
      return null;
    }

    const [dice1, dice2] = diceRoll.values;
    const combined = dice1 + dice2;

    const dice1HasMoves = availableMoves[dice1]?.length > 0;
    const dice2HasMoves = availableMoves[dice2]?.length > 0;
    const combinedHasMoves = availableMoves[combined]?.length > 0;

    return (
      <div className="naija-dice-options">
        <p className="dice-instruction">Choose your move:</p>
        <div className="dice-dots-container">
          <div
            className={`dice-dot ${selectedDiceValue === dice1 ? 'selected' : ''} ${
              dice1HasMoves ? 'has-moves' : 'no-moves'
            }`}
            onClick={() => dice1HasMoves && onDiceValueSelect(dice1)}
          >
            <span className="dot-value">{dice1}</span>
            <span className="dot-label">Dice 1</span>
          </div>

          <div
            className={`dice-dot ${selectedDiceValue === combined ? 'selected' : ''} ${
              combinedHasMoves ? 'has-moves' : 'no-moves'
            }`}
            onClick={() => combinedHasMoves && onDiceValueSelect(combined)}
          >
            <span className="dot-value">{combined}</span>
            <span className="dot-label">Combined</span>
          </div>

          <div
            className={`dice-dot ${selectedDiceValue === dice2 ? 'selected' : ''} ${
              dice2HasMoves ? 'has-moves' : 'no-moves'
            }`}
            onClick={() => dice2HasMoves && onDiceValueSelect(dice2)}
          >
            <span className="dot-value">{dice2}</span>
            <span className="dot-label">Dice 2</span>
          </div>
        </div>

        {selectedDiceValue && (
          <p className="selected-instruction">
            ✨ Click a highlighted piece to move {selectedDiceValue} spaces
          </p>
        )}
      </div>
    );
  };

  const renderClassicMoveOptions = () => {
    if (!diceRoll || moveOptions.length === 0 || gameVariant !== 'classic') {
      return null;
    }

    return (
      <div className="move-options">
        <h4>Choose your move:</h4>
        <div className="move-options-list">
          {moveOptions.map((option, index) => (
            <button
              key={index}
              className={`move-option ${selectedMoveOption === option ? 'selected' : ''}`}
              onClick={() => onMoveOptionSelect(option)}
            >
              <span className="move-description">{option.description}</span>
              <span className="dice-used">
                {option.combinedMove
                  ? `Combined: ${option.diceUsed.join('+')}`
                  : `Use: ${option.diceUsed[0]}`}
              </span>
            </button>
          ))}
        </div>

        {selectedMoveOption && (
          <button className="execute-move-btn" onClick={onExecuteMove}>
            Execute Move
          </button>
        )}
      </div>
    );
  };

  const renderInstructions = () => {
    if (isRolling) {
      return <p className="dice-instruction">Rolling...</p>;
    }

    if (!diceRoll && canRoll) {
      return (
        <p className="dice-instruction">
          {gameVariant === 'naija'
            ? 'Tap to roll two dice'
            : 'Tap to roll dice'}
        </p>
      );
    }

    if (diceRoll?.isEndTurn) {
      return (
        <p className="dice-instruction warning">
          Three 6s in a row! Turn ended.
        </p>
      );
    }

    if (diceRoll && moveOptions.length === 0 && gameVariant === 'classic') {
      return <p className="dice-instruction">No valid moves!</p>;
    }

    if (diceRoll && gameVariant === 'naija') {
      const totalMoves = Object.values(availableMoves).reduce(
        (sum, moves) => sum + moves.length,
        0
      );
      if (totalMoves === 0) {
        return <p className="dice-instruction">No valid moves!</p>;
      }
    }

    if (diceRoll && gameVariant === 'classic' && moveOptions.length > 0) {
      return <p className="dice-instruction">Select a piece to move</p>;
    }

    return null;
  };

  return (
    <div className={`dice-component ${gameVariant}`}>
      {renderDice()}
      {renderInstructions()}

      {gameVariant === 'naija' && renderNaijaDiceOptions()}

      {gameVariant === 'classic' && renderClassicMoveOptions()}

      {diceRoll?.canRollAgain && !selectedMoveOption && !selectedDiceValue && (
        <div className="roll-again-indicator">🎉 You can roll again!</div>
      )}
    </div>
  );
};

export default DiceComponent;
